import Vue from 'vue';
import { axios } from '@/axios.config';
import moment from 'moment/moment';
import { generateDateArray } from '@/lib/dates';

export const state = {
  /**
   * reports are added to this object in format:
   * reports:
   *   [reportHash]: [
   *     { name: [subjectName], value: ... }
   *   ]
   */
  reports: {},
  requests: {},
  range: {
    start: '',
    end: '',
  },
  dimension: '',
};

/**
 * Creates a unique identifier for the given usage report
 * @param {string} owner
 * @param {string} [ownerId]
 * @param {string} dimension
 * @param {string} vendor
 * @param {Moment} from
 * @param {Moment} to
 * @param {string} subject
 * @returns {string}
 */
export function hash({ owner, ownerId, dimension, vendor, from, to, subject }) {
  from = moment(from).format('YYYY-MM-DD');
  to = moment(to).format('YYYY-MM-DD');
  return `${owner}-${ownerId ? `${ownerId}-` : ''}${dimension}-${from}-${to}-${subject}-${vendor}`;
}

export const actions = {
  /**
   * Fetches the usage data for the given owner and dimension range.
   * @param {string} usageReport.resourceType - Resource type of the data 'account'|'tag'
   * @param {string} usageReport.owner - Owner type of the data 'company'|'account'|'region'|'group'|'tag'
   * @param {string} usageReport.ownerId - Owner of the data
   * @param {string} usageReport.dimension - Dimension for the data range 'monthly'|'daily'
   * @param {string} usageReport.subject - Subject of data, available options 'service', 'account'
   * @param {string} usageReport.vendor - Cloud vendor (default: 'aws')
   * @param {Moment} usageReport.from - From date
   * @param {Moment} usageReport.to - To date
   */
  async fetch({ state, commit }, usageReport) {
    const reqHash = hash(usageReport);
    if (state.requests[reqHash] && state.requests[reqHash].inProgress) {
      return;
    }

    let { resourceType, owner, ownerId, dimension, subject, from, to, vendor } = usageReport;
    from = moment(from);
    to = moment(to);

    // daily or monthly, rangeをSTATEにセット
    commit('updateDimension', dimension);
    commit('updateRange', { start: from, end: to });

    /**
     * Generate URL Function
     * @param {string} resourceType - account | tag
     * @param {string} owner
     * @param {string} ownerId
     * @param {string} dimension
     * @returns {string}
     */
    const getUrl = ({ resourceType, owner, ownerId, dimension }) => {
      if (resourceType === 'account') {
        if (owner === 'company') {
          // For Dashboard graph
          return `/reports/company/${dimension}`;
        } else {
          // For Usage Report graph
          return `/reports/${owner}/${ownerId}/${dimension}`;
        }
      }

      if (resourceType === 'tag') {
        // Dashboardグラフ
        if (owner === 'company') {
          // For Dashboard graph
          return `/crosstag/reports/company/${dimension}`;
        } else {
          // For Usage Report graph
          return `/crosstag/reports/${owner}/${ownerId}/${dimension}`;
        }
      }
    };

    const url = getUrl({ resourceType, owner, ownerId, dimension });
    const params = {
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
      by: subject,
      vendor: vendor,
    };

    const cancelToken = new axios.CancelToken(() => {});

    commit('fetchStart', { usageReport, reqHash, cancelToken });

    return axios
      .get(url, {
        params: params,
      })
      .then(({ data }) => {
        commit('fetchSucceed', { reqHash });
        commit('updateUsageReport', { data, vendor, reqHash });
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          commit('fetchCanceled', { reqHash });
          return;
        }

        commit('fetchFailed', { reqHash, err });
        throw err;
      });
  },
};

export const mutations = {
  updateRange(state, range) {
    state.range.start = range.start;
    state.range.end = range.end;
  },

  updateDimension(state, type) {
    state.dimension = type;
  },

  updateUsageReport(state, { data, vendor, reqHash }) {
    const awsData = data[vendor];

    const nitems = ds => ds.date.length;
    const biggestDatasetIdx = awsData.reduce(
      (maxId, ds, i) => (nitems(ds) > nitems(awsData[maxId]) ? i : maxId),
      0
    );

    if (!awsData[biggestDatasetIdx]) {
      Vue.set(state.reports, reqHash, []);
    } else {
      const toDate = datestr =>
        moment(datestr, 'YYYY-MM-DD')
          .startOf('date')
          .toDate();

      // const biggestDataset = awsData[biggestDatasetIdx].date.map(item => {
      //   return {
      //     ...item,
      //     date: toDate(item.date),
      //   };
      // });

      // const maxDataPoints = biggestDataset.length;

      // eslint-disable-next-line
      const datasets = awsData.map((ds, dsIdx) => {
        let total = 0;
        // FIXME: api shouldn't return "date", naming should be "data"
        ds.date.forEach(item => {
          total += item.unblended_cost;
          item.date = toDate(item.date);
        });

        ds = {
          id: ds.id,
          name: ds.name,
          data: ds.date,
          total: total,
        };

        // Not every dataset has the same length of data. For example not all the services
        // are used in the same time. Some of them can be activated and deactivated. This is
        // why we have missing data points in the reports api response. We should fill those
        // gaps with $0 costs.

        // if (dsIdx === biggestDatasetIdx || ds.data.length === maxDataPoints) {
        //   return ds;
        // }

        const newDataPoints = [];

        /**
         * 2020/04/10追加
         * チャートのデータに１つのサービスでも1ヶ月分のデータすべての日にデータが入っている必要があったが、
         *    それがない場合の対策。
         *    （例えば、全サービスの最もデータがあるもので、10日分しかデータが入っていないなど。）
         *
         * 一ヶ月分の日付配列を作成し、それを使って足りない部分を補う。
         */
        const start = state.range.start;
        const end = state.range.end;

        let type;
        let format;
        if (state.dimension === 'daily') {
          type = 'days';
          format = 'YYYY-MM-DD';
        }
        if (state.dimension === 'monthly') {
          type = 'months';
          format = 'YYYY-MM';
        }

        // 一ヶ月分の日付配列
        const baseDates = generateDateArray(start, end, type, format);

        baseDates.forEach((item, itemIdx) => {
          const x = ds.data.find(x => {
            const dsData = moment(x.date).format(format);
            const baseDate = item;
            return dsData === baseDate;
          });

          if (!x) {
            newDataPoints.push({
              index: itemIdx,
              data: {
                date: toDate(item),
                blended_cost: 0,
                unblended_cost: 0,
                true_unblended_cost: 0,
              },
            });
          }
        });

        // biggestDataset.forEach((item, itemIdx) => {
        //   const x = ds.data.find(x => x.date.getTime() === item.date.getTime());
        //   if (!x) {
        //     newDataPoints.push({
        //       index: itemIdx,
        //       data: { date: item.date, blended_cost: 0, unblended_cost: 0, true_unblended_cost: 0 },
        //     });
        //   }
        // });

        newDataPoints.forEach(({ index, data }) => {
          ds.data.splice(index, 0, data);
        });

        return ds;
      });

      const dsTotal = ds => ds.data.reduce((sum, i) => sum + i.unblended_cost, 0);

      datasets.sort((a, b) => {
        const atotal = dsTotal(a);
        const btotal = dsTotal(b);
        return atotal > btotal ? -1 : atotal < btotal ? 1 : 0;
      });

      Vue.set(state.reports, reqHash, datasets);
    }
  },

  fetchStart(state, { reqHash, cancelToken }) {
    Vue.set(state.requests, reqHash, {
      inProgress: true,
      error: null,
      canceled: false,
      cancelToken,
    });
  },

  fetchSucceed(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: false,
      cancelToken: null,
    });
  },

  fetchCanceled(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: true,
      cancelToken: null,
    });
  },

  fetchFailed(state, { reqHash, err }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: err,
      canceled: false,
      cancelToken: null,
    });
  },
};
